/**
 * Mode switch for a performance-tag condition — the perf twin of the FO
 * page's FlowTagConditionRow: with `compareTo` set the row is the shared
 * compare editor (the RHS metric is the DEFAULT threshold — override rows
 * still apply per scope); without it, the classic ConditionRow plus the
 * same dashed ⇄ icon the FO pages use, seeding a DIRECT comparison (no %).
 * Override tables survive the switch — each row pins a number or compares.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC } from 'react';
import { ArrowLeftRight } from 'lucide-react';

import { CompareConditionRow, defaultCompareTo } from '@/features/tag-rule-compare';
import type { TagRuleScope } from '@/features/tag-rule-scope';
import type { TagRuleCondition } from '@/hooks';

import { ConditionRow } from './ConditionRow';

interface ConditionModeRowProps {
  condition: TagRuleCondition;
  onChange: (c: TagRuleCondition) => void;
  onRemove: () => void;
  canRemove: boolean;
  scope?: TagRuleScope;
  fields?: ReadonlyArray<{ key: string; label: string }>;
}

export const ConditionModeRow: FC<ConditionModeRowProps> = ({
  condition,
  onChange,
  onRemove,
  canRemove,
  scope,
  fields,
}) => {
  if (condition.compareTo) {
    return (
      <CompareConditionRow
        condition={condition}
        onChange={onChange}
        onRemove={onRemove}
        canRemove={canRemove}
        fields={fields}
      />
    );
  }
  return (
    <ConditionRow
      condition={condition}
      // Spread the ORIGINAL first so fields ConditionRow never heard of
      // survive its write-backs; its explicit-undefined clears still win.
      onChange={(c) => onChange({ ...condition, ...c })}
      onRemove={onRemove}
      canRemove={canRemove}
      scope={scope}
      fields={fields}
      action={
        <button
          type="button"
          onClick={() =>
            // Overrides stay: each row independently pins a number or
            // carries its own comparison.
            onChange({ ...condition, compareTo: defaultCompareTo(condition.field) })
          }
          title="Compare to another metric instead of a fixed value"
          aria-label="Compare to a metric instead of a fixed value"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
        >
          <ArrowLeftRight className="h-4 w-4" />
        </button>
      }
    />
  );
};

export default ConditionModeRow;
